import React, { useState, memo, Fragment, useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Link, useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Button as MuiButton, Tooltip } from '@mui/material';

import {
  selectCurrentUser,
  selectToken,
} from '../../redux/user/userSelectors';
import { showAlert } from '../../redux/alert/alertActions';
import { signInStart } from '../../redux/user/userActions';
import { stopImpersonation as stopImpersonationService } from '../../services/superAdminService';

import useScrollPositionThrottled from '../../hooks/useScrollPositionThrottled';

import { ReactComponent as LogoCamera } from '../../assets/svg/logo-camera.svg';
import SearchBox from '../SearchBox/SearchBox';
import NewPostButton from '../NewPost/NewPostButton/NewPostButton';
import NotificationButton from '../Notification/NotificationButton/NotificationButton';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';

const Header = memo(({ currentUser, token, showGlobalAlert, signInStart }) => {
  const [shouldMinimizeHeader, setShouldMinimizeHeader] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  // Shrink header height and remove logo on scroll
  useScrollPositionThrottled(({ currentScrollPosition }) => {
    if (window.outerWidth > 600) {
      setShouldMinimizeHeader(currentScrollPosition > 100);
    }
  });

  const headerClassNames = classNames({
    header: true,
    'header--small': shouldMinimizeHeader,
  });

  const handleStopImpersonation = useCallback(async () => {
    if (!token || restoring) {
      return;
    }

    setRestoring(true);
    try {
      const response = await stopImpersonationService(token);
      showGlobalAlert('Restored your administrator session.');
      signInStart(null, null, response.token);
      history.push('/super-admin');
    } catch (error) {
      showGlobalAlert(
        (error && error.message) ||
          'Unable to restore your administrator session.'
      );
    } finally {
      setRestoring(false);
    }
  }, [token, restoring, showGlobalAlert, signInStart]);

  return (
    <header className={headerClassNames}>
      <div className="header__content">
        <Link to="/" className="header__logo">
          <div className="header__logo-image">
            <LogoCamera />
          </div>
          <div className="header__logo-header">
            <h3 className="heading-logo">Team Up Sport</h3>
          </div>
        </Link>
        <SearchBox />
        <div className="header__icons">
          {currentUser ? (
            <Fragment>
              {currentUser?.isImpersonating && (
                <Tooltip
                  title={
                    currentUser.originalAdminUsername
                      ? `Impersonating on behalf of ${currentUser.originalAdminUsername}`
                      : 'Impersonating another user'
                  }
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <MuiButton
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={handleStopImpersonation}
                      disabled={restoring}
                      sx={{
                        mr: 2,
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {restoring ? 'Restoring…' : 'Return to admin'}
                    </MuiButton>
                  </span>
                </Tooltip>
              )}
              <Link to="/explore">
                <Icon
                  icon={pathname === '/explore' ? 'compass' : 'compass-outline'}
                />
              </Link>
              {(currentUser.userType || '').toUpperCase() === 'ATHLETE' && (
                <Link to="/creator/subscriptions">
                  <Icon
                    icon={
                      pathname === '/creator/subscriptions'
                        ? 'podium'
                        : 'podium-outline'
                    }
                  />
                </Link>
              )}
              <Link to="/subscriptions">
                <Icon
                  icon={
                    pathname === '/subscriptions'
                      ? 'wallet'
                      : 'wallet-outline'
                  }
                />
              </Link>
              {currentUser?.roles?.includes('SuperAdmin') && (
                <Link to="/super-admin">
                  <Icon
                    icon={
                      pathname === '/super-admin'
                        ? 'shield-checkmark'
                        : 'shield-checkmark-outline'
                    }
                  />
                </Link>
              )}
              <Link to="/activity">
                <Icon
                  icon={pathname === '/activity' ? 'heart' : 'heart-outline'}
                />
              </Link>
              <NotificationButton />
              <Link to={`/${currentUser.username}`}>
                <Icon
                  icon={
                    pathname === `/${currentUser.username}`
                      ? 'person-circle'
                      : 'person-circle-outline'
                  }
                />
              </Link>
              {(currentUser.userType || '').toUpperCase() === 'ATHLETE' && (
                <NewPostButton />
              )}
            </Fragment>
          ) : (
            <Fragment>
              <Link style={{ marginRight: '1rem' }} to="/login">
                <Button>Log In</Button>
              </Link>
              <Link to="/signup">
                <h3 className="heading-3 heading--button color-blue">
                  Sign Up
                </h3>
              </Link>
            </Fragment>
          )}
        </div>
      </div>
    </header>
  );
});

Header.whyDidYouRender = true;

const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
  token: selectToken,
});

const mapDispatchToProps = {
  showGlobalAlert: showAlert,
  signInStart,
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
